import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { CTA, PRICE_GUIDE, PRICING_NOTE, waInspectorHref } from "@/lib/business";

export function PriceGuide({ onQuote }) {
  return (
    <section id="pricing" className="py-20 lg:py-24 bg-white scroll-mt-[130px] lg:scroll-mt-[190px]" data-testid="pricing-section">
      <div className="wrap">
        <SectionHead center eyebrow="Price guide" title="Transparent starting prices by property type."
          sub="A clear starting point for standard inspections. Fedari confirms the exact fixed fee and scope before you book." />
        <div className="grid lg:grid-cols-[minmax(0,720px),340px] gap-8 items-center justify-center">
          <div className="rounded-[16px] border border-line overflow-hidden divide-y divide-line" data-testid="price-guide">
            {PRICE_GUIDE.map((p) => (
              <div key={p.type} className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 bg-white hover:bg-soft transition-colors">
                <span className="text-[0.98rem] font-medium text-ink min-w-0">{p.type}</span>
                <span className="font-display text-[0.95rem] sm:text-[1.15rem] text-blue text-right leading-tight flex-none">{p.price}</span>
              </div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <figure className="overflow-hidden rounded-[16px] border border-line bg-soft max-w-[340px] mx-auto">
              <picture>
                <source type="image/avif" srcSet="/images/fedari-plaque-villa-800.avif 800w, /images/fedari-plaque-villa-1536.avif 1536w" sizes="(max-width: 1024px) 100vw, 340px" />
                <source type="image/webp" srcSet="/images/fedari-plaque-villa-800.webp 800w, /images/fedari-plaque-villa-1536.webp 1536w" sizes="(max-width: 1024px) 100vw, 340px" />
                <img src="/images/fedari-plaque-villa-800.webp" alt="Fedari 'Inspected by Fedari' plaque at a villa entrance in Dubai" width="1536" height="1024" loading="lazy" decoding="async" className="w-full h-auto object-cover" data-testid="pricing-plaque" />
              </picture>
              <figcaption className="px-4 py-2.5 text-[0.78rem] text-muted">Inspected by Fedari — documented findings for your follow-up</figcaption>
            </figure>
          </Reveal>
        </div>
        <p className="text-[0.84rem] text-muted mt-5 text-center max-w-[70ch] mx-auto">{PRICING_NOTE}</p>
        <div className="flex justify-center mt-7">
          <button onClick={() => onQuote()} data-testid="pricing-quote-btn" className="inline-flex items-center gap-2 bg-blue text-white px-7 py-4 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">{CTA.primary} <ArrowRight size={16} /></button>
        </div>
      </div>
    </section>
  );
}

export function FinalCta({ onQuote }) {
  return (
    <section className="py-20 lg:py-24 bg-soft border-t border-line text-center" data-testid="final-cta">
      <div className="wrap">
        <Reveal>
          <span className="eyebrow">Before you accept</span>
          <h2 className="text-[clamp(2rem,4.6vw,3.2rem)] max-w-[20ch] mx-auto mt-3 mb-4">Before you accept the property, inspect it.</h2>
          <p className="text-muted max-w-[52ch] mx-auto mb-7 text-[1.08rem]">Share the property type, location, size and preferred date. We will confirm the scope, price and available inspection times.</p>
          <div className="flex gap-3 justify-center flex-wrap mb-5">
            <button onClick={() => onQuote()} className="inline-flex items-center gap-2 bg-blue text-white px-7 py-4 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors" data-testid="final-quote-btn">{CTA.primary} <ArrowRight size={17} /></button>
            <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-4 rounded-[10px] border border-line bg-white text-ink font-semibold hover:border-wa hover:text-wa transition-colors">{CTA.whatsapp}</a>
          </div>
          <p className="font-mono-jb text-[0.76rem] tracking-wider text-muted">Apartments · Villas · Townhouses · New Builds · Resale · Commercial</p>
        </Reveal>
      </div>
    </section>
  );
}
